import { FC, useEffect, useRef, useState, type CSSProperties } from 'react';
import { V4 as C } from '@components/v4/tokens';
import { Z } from '@components/v4';
import { OVERLAY_PRIORITY, useOverlaySlot } from '@context/overlayGate';
import useTracking, { TRACK_EVENT_NAMES } from '@utils/hooks/useTracking';
import { usePaulina } from './PaulinaProvider';

/**
 * PaulinaWelcomeCoachmark — re-introducción DISCRETA de Paulina: un saludo "una sola vez por
 * identidad, para siempre" anclado a la entrada de Paulina en el header móvil (bajo el avatar).
 *
 * Por qué existe: Paulina monta para TODOS los comercios (rollout 'agent-chat' al 100% GA), pero su
 * proactividad recurrente (tips/nudges) está APAGADA por PAULINA_NUDGES_ENABLED (usePaulinaChat) —
 * los tips tenían baja adopción y se percibían molestos. Este coachmark NO revive esos tips: es una
 * superficie NUEVA e independiente que solo se muestra UNA vez (el primer saludo) y nunca más, para
 * que el avatar del header (que no tiene label, a diferencia de la pill de desktop) sea descubrible.
 *
 * Móvil es la prioridad: el avatar del header no lleva texto, así que es el hueco real de
 * descubrimiento. Desktop se OMITE a propósito: la pill ya dice "Paulina" (no hay hueco que llenar).
 *
 * Disciplina (playbook):
 *  - PRJ-001: el "visto" se persiste en una llave DURABLE de localStorage con prefijo
 *    `paulina_welcome_seen` que el barrido de logout NO matchea (borra `pau_nudge_`/
 *    `pau_hints_last_eval` y, en sessionStorage, `agent_chat_`) y que resetStore() no toca (solo
 *    limpia slices de Redux). Si logout la borrara, el saludo reaparecería en cada login = intrusivo.
 *  - PRJ-004: 100% inline styles con tokens de V4, cero Tailwind.
 *  - PRJ-009: pasa por el OverlayGate (useOverlaySlot) para respetar "un nag por sesión" y ceder ante
 *    un pedido entrante; el z-index sale del registro (Z.paulinaPill), nunca ad-hoc.
 *
 * Kill-switch reversible: PAULINA_WELCOME_ENABLED (misma disciplina que PAULINA_NUDGES_ENABLED) —
 * cambiar a `false` retira por completo este coachmark sin más cambios.
 */

// Kill-switch reversible del coachmark de bienvenida. `false` = superficie apagada (nunca se muestra),
// INDEPENDIENTE del kill-switch de tips/nudges (PAULINA_NUDGES_ENABLED): esto no es un tip recurrente.
export const PAULINA_WELCOME_ENABLED: boolean = true;

// Auto-ocultado no bloqueante (ms). Al vencer, se marca "visto" y se cierra igual que un tap/X.
const WELCOME_AUTO_HIDE_MS = 7000;

// Id del slot en el OverlayGate (coordinación "uno a la vez" + cesión ante pedido).
const WELCOME_SLOT_ID = 'paulina-welcome';

// LECCIÓN heredada de las demás superficies de Paulina (PaulinaInvite / PaulinaNudgeTeaserMobile):
// base `opacity:1` y animar SOLO `translateY` — un keyframe con opacidad dentro de un contenedor con
// transform puede quedar stuck en 0. Solo corre bajo prefers-reduced-motion:no-preference.
const KF_ID = 'plick-paulina-welcome-kf';
if (typeof document !== 'undefined' && !document.getElementById(KF_ID)) {
  const st = document.createElement('style');
  st.id = KF_ID;
  st.textContent =
    '@media (prefers-reduced-motion: no-preference){' +
    '@keyframes plick-paulina-welcome-in{from{transform:translateY(-6px)}to{transform:translateY(0)}}' +
    '.plick-paulina-welcome-anim{animation:plick-paulina-welcome-in 220ms ease-out both}}';
  document.head.appendChild(st);
}

// Sufijo de identidad (comercio, usuario) — mismo patrón y fallback 'anon' que identitySuffix de
// usePaulinaChat, para namespacear el "visto" por identidad (cada usuario ve su propio saludo).
const identitySuffix = (): string => {
  try {
    const c = localStorage.getItem('id_commerce') || 'anon';
    const u = localStorage.getItem('id_user') || 'anon';
    return `${c}:${u}`;
  } catch {
    return 'anon:anon';
  }
};

// Llave DURABLE del "visto" (PRJ-001). El prefijo `paulina_welcome_seen` NO lo barre el logout ni lo
// limpia resetStore, así que el saludo se muestra UNA vez por identidad para siempre.
export const welcomeStorageKey = (): string => `paulina_welcome_seen:${identitySuffix()}`;

export const welcomeSeen = (): boolean => {
  try {
    return localStorage.getItem(welcomeStorageKey()) === '1';
  } catch {
    // Sin storage (incógnito/sandbox): tratamos como "ya visto" para no insistir sin poder recordarlo.
    return true;
  }
};

export const markWelcomeSeen = (): void => {
  try {
    localStorage.setItem(welcomeStorageKey(), '1');
  } catch {
    /* sin storage: no persiste; el auto-ocultado + el gate del OverlayGate evitan que insista */
  }
};

// Predicado puro de elegibilidad (separado para testear la rama del kill-switch sin renderizar).
// `seen` y `flagEnabled` tienen default para el call-site de producción; el test los fuerza.
export const isWelcomeEligible = (
  enabled: boolean,
  seen: boolean = welcomeSeen(),
  flagEnabled: boolean = PAULINA_WELCOME_ENABLED,
): boolean => flagEnabled && enabled && !seen;

const DismissIcon: FC = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

export const PaulinaWelcomeCoachmark: FC = () => {
  const paulina = usePaulina();
  const enabled = Boolean(paulina?.enabled);
  const { trackEvent } = useTracking();

  // Snapshot de "visto" al montar: evita que la escritura del "visto" (que ocurre AL mostrarse)
  // vuelva a evaluar la elegibilidad y esconda el coachmark en el mismo montaje.
  const [alreadySeen] = useState<boolean>(() => welcomeSeen());
  const [closed, setClosed] = useState(false);
  const eligible = isWelcomeEligible(enabled, alreadySeen) && !closed;

  const granted = useOverlaySlot(WELCOME_SLOT_ID, OVERLAY_PRIORITY.paulinaWelcome, eligible);

  // Refs siempre-actuales (trackEvent viene inestable de useTracking; el close se referencia desde
  // el timer). Mismo patrón que onDismissRef en PaulinaNudgeTeaserMobile.
  const trackRef = useRef(trackEvent);
  trackRef.current = trackEvent;

  const closedRef = useRef(false);
  const close = (motivo: 'timeout' | 'tap' | 'dismiss') => {
    if (closedRef.current) return;
    closedRef.current = true;
    setClosed(true);
    trackRef.current(TRACK_EVENT_NAMES.PaulinaWelcomeDismissed, { motivo, surface: 'mobile' });
  };
  const closeRef = useRef(close);
  closeRef.current = close;

  // Al CONCEDERSE el slot (se muestra de verdad, no antes): marcar "visto" para siempre + telemetría
  // + armar el auto-ocultado. El shownRef evita que el doble-montaje de StrictMode lo duplique. NO se
  // marca "visto" si el gate difiere el coachmark (pedido/otro overlay): así se mostrará otra sesión.
  const shownRef = useRef(false);
  useEffect(() => {
    if (!granted || shownRef.current) return;
    shownRef.current = true;
    markWelcomeSeen();
    trackRef.current(TRACK_EVENT_NAMES.PaulinaWelcomeShown, { surface: 'mobile' });
    const t = window.setTimeout(() => closeRef.current('timeout'), WELCOME_AUTO_HIDE_MS);
    return () => window.clearTimeout(t);
  }, [granted]);

  if (!granted) return null;

  // Tap en el cuerpo del coachmark → abre el chat (convierte el saludo en acción) y lo cierra. La X
  // solo cierra. Cualquier cierre marca "visto" (ya se marcó al mostrarse) y emite telemetría.
  const openChat = () => {
    paulina?.toggleOpen();
    closeRef.current('tap');
  };

  // Anclado bajo el avatar del header móvil (header = 52px). Caret hacia ARRIBA apuntando al avatar
  // (a la izquierda de la campana, ~right 44px). Molde visual de PaulinaInvite (card), compacto.
  const bubble: CSSProperties = {
    position: 'fixed',
    top: 58,
    right: 10,
    zIndex: Z.paulinaPill,
    width: 'min(80vw, 264px)',
    background: C.primary,
    color: C.white,
    borderRadius: 12,
    padding: '12px 14px',
    boxShadow: '0 14px 36px rgba(37,55,70,.32)',
    cursor: 'pointer',
    opacity: 1,
  };
  const caret: CSSProperties = {
    position: 'absolute',
    top: -5,
    right: 44,
    width: 12,
    height: 12,
    background: C.primary,
    transform: 'rotate(45deg)',
    borderRadius: 2,
  };

  return (
    <div
      role="dialog"
      aria-label="Conoce a Paulina, tu asistente"
      className="plick-paulina-welcome-anim"
      style={bubble}
      onClick={openChat}
    >
      <span aria-hidden="true" style={caret} />
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
        <p style={{ margin: 0, fontSize: 13.5, fontWeight: 700, lineHeight: 1.3 }}>👋 Soy Paulina, tu asistente</p>
        <button
          type="button"
          aria-label="Descartar"
          onClick={(e) => {
            e.stopPropagation();
            closeRef.current('dismiss');
          }}
          style={{
            flexShrink: 0,
            background: 'transparent',
            border: 0,
            color: 'rgba(255,255,255,.7)',
            cursor: 'pointer',
            padding: 0,
            marginTop: -1,
            display: 'inline-flex',
          }}
        >
          <DismissIcon />
        </button>
      </div>
      <p style={{ margin: '4px 0 0', fontSize: 12, lineHeight: 1.45, color: 'rgba(255,255,255,.82)' }}>
        Tócame cuando tengas una duda y te echo la mano.
      </p>
    </div>
  );
};
